// The desk's settings, as the engine's category registry holds them.
//
// `WRITING-DESK-COMPLETE-SPEC-V1.md` §9.1: settings are engine-owned, per-tenant,
// and tier-gated, rendered by Seitiate+ over `@seitiate/settings-ui`. §9.3: under
// the 2026-08-22 tenant-is-a-person ruling, per-tenant settings ARE per-person
// settings, so there is no second preference store and nothing here is scoped to
// a device or a browser.
//
// Project-scoped fields (custody tier, citation style, retention, template) are
// the DEFAULTS a new project is created with; once a project exists, its own
// settings travel with it in the project representation.

export const DESK_SETTINGS_CATEGORY = 'writing_desk';

export type CustodyTier = 'managed' | 'sovereign' | 'local_only';

export interface DeskSettings {
  /** Project default. `managed` per the custody ruling. */
  custody_tier: CustodyTier;
  /** Project default. */
  cloud_sync: boolean;
  /** Project default: a CSL style id. */
  citation_style: string;
  /** Person-scoped. Three seconds, per the desk spec's autosave cadence. */
  autosave_interval_s: number;
  /** Project default. */
  snapshot_retention_count: number;
  /** Project default. */
  snapshot_retention_days: number;
  /** Person-scoped. On: a paste asks the writer what they just did. */
  declaration_prompt_on_paste: boolean;
  /** Person-scoped. Off: marks are inspected deliberately, not always on show. */
  provenance_view_default: boolean;
  /** Person-scoped. The STT route; the seam is ruled, the capture is S3. */
  dictation_provider: string;
  /** Project default. Null until the export templates land (S6). */
  export_template: string | null;
  /** Person-scoped. Source-view writeback is not built in this slice. */
  source_view_editable: boolean;
  /** Person-scoped. The agent pane arrives with `desk-assistant.v1`. */
  agent_model_default: string;
}

export const DESK_SETTINGS_DEFAULTS: DeskSettings = {
  custody_tier: 'managed',
  cloud_sync: true,
  citation_style: 'apa',
  autosave_interval_s: 3,
  snapshot_retention_count: 200,
  snapshot_retention_days: 365,
  declaration_prompt_on_paste: true,
  provenance_view_default: false,
  dictation_provider: 'managed',
  export_template: null,
  source_view_editable: false,
  agent_model_default: 'auto',
};

/**
 * Read a settings payload into the typed shape, falling back per field.
 *
 * Per-field rather than all-or-nothing: an engine that predates one of these
 * fields should still give the writer the rest of their settings, and a value
 * the engine does not recognise is the engine's to reject on write, not
 * something to drop silently on read.
 */
export function readDeskSettings(payload: Record<string, unknown> | undefined | null): DeskSettings {
  const source = payload ?? {};
  const bool = (key: keyof DeskSettings, fallback: boolean): boolean =>
    typeof source[key] === 'boolean' ? (source[key] as boolean) : fallback;
  const text = (key: keyof DeskSettings, fallback: string): string =>
    typeof source[key] === 'string' && source[key] !== '' ? (source[key] as string) : fallback;
  const count = (key: keyof DeskSettings, fallback: number): number =>
    typeof source[key] === 'number' && Number.isFinite(source[key]) ? (source[key] as number) : fallback;

  const custody = text('custody_tier', DESK_SETTINGS_DEFAULTS.custody_tier);
  return {
    custody_tier: (['managed', 'sovereign', 'local_only'] as const).includes(custody as CustodyTier)
      ? (custody as CustodyTier)
      : DESK_SETTINGS_DEFAULTS.custody_tier,
    cloud_sync: bool('cloud_sync', DESK_SETTINGS_DEFAULTS.cloud_sync),
    citation_style: text('citation_style', DESK_SETTINGS_DEFAULTS.citation_style),
    autosave_interval_s: count('autosave_interval_s', DESK_SETTINGS_DEFAULTS.autosave_interval_s),
    snapshot_retention_count: count(
      'snapshot_retention_count',
      DESK_SETTINGS_DEFAULTS.snapshot_retention_count,
    ),
    snapshot_retention_days: count(
      'snapshot_retention_days',
      DESK_SETTINGS_DEFAULTS.snapshot_retention_days,
    ),
    declaration_prompt_on_paste: bool(
      'declaration_prompt_on_paste',
      DESK_SETTINGS_DEFAULTS.declaration_prompt_on_paste,
    ),
    provenance_view_default: bool(
      'provenance_view_default',
      DESK_SETTINGS_DEFAULTS.provenance_view_default,
    ),
    dictation_provider: text('dictation_provider', DESK_SETTINGS_DEFAULTS.dictation_provider),
    export_template:
      typeof source.export_template === 'string' ? (source.export_template as string) : null,
    source_view_editable: bool('source_view_editable', DESK_SETTINGS_DEFAULTS.source_view_editable),
    agent_model_default: text('agent_model_default', DESK_SETTINGS_DEFAULTS.agent_model_default),
  };
}
